import { Search } from '@mui/icons-material';
import { Box, styled, formHelperTextClasses } from '@mui/material';
import React, { ReactNode, useContext } from 'react';
import { FormTextField, FormTextFieldProps } from '../../~controls/FormControls/FormTextField';
import {
    FormSelect,
    FormSelectProps,
    FormSelectOption,
    renderSelectOptions,
} from '../../~controls/FormControls/FormSelect';
import { fieldNames } from '../schema';
import { TradingFormContext } from '../TradingFormContext';

export type SymbolSelectProps = {
    symbol?: string;
    isMutualFund?: boolean;
    isBond?: boolean;
    isValid?: boolean;
    children?: ReactNode;
    formTextFieldProps?: Partial<FormTextFieldProps>;
    formSelectProps?: Partial<FormSelectProps>;
    symbols?: FormSelectOption[];
    onSymbolSelect?: (symbol: string) => void;
};

const StyledFormTextField = styled(FormTextField)(({ theme }) => ({
    [`& .${formHelperTextClasses.root}`]: {
        display: 'none',
    },
    '& svg': {
        margin: theme.spacing(0, 1, 0, 0),
        transform: theme.rtl ? 'scaleX(-1)' : undefined,
    },
}));

export const SymbolSelect: React.FunctionComponent<SymbolSelectProps> = ({
    symbol,
    isMutualFund,
    isBond,
    isValid,
    children,
    formTextFieldProps,
    formSelectProps,
    symbols,
    onSymbolSelect,
}) => {
    const {
        meta: { bondTrading, mutualFundTrading },
        translate,
    } = useContext(TradingFormContext);

    const validate = () => {
        let error = '';

        if (mutualFundTrading && !isMutualFund) {
            error = translate('symbolSelect.errors.notMutualFundSymbol');
        }

        if (bondTrading && !isBond) {
            error = translate('symbolSelect.errors.notBondSymbol');
        }

        if (isValid === false) {
            error = translate('symbolSelect.errors.notValidSymbol');
        }
        return error;
    };

    return (
        <React.Fragment>
            {symbols ? (
                <FormSelect
                    variant="outlined"
                    size="small"
                    {...formSelectProps}
                    name={fieldNames.symbol}
                    displayEmpty
                    disableUnderline={true}
                    validate={validate}
                    onChange={onSymbolSelect}
                >
                    {renderSelectOptions(symbols)}
                </FormSelect>
            ) : (
                <StyledFormTextField
                    variant="outlined"
                    placeholder={translate('symbolSelect.placeholder')}
                    allCaps
                    size="small"
                    InputProps={{
                        disableUnderline: true,
                        startAdornment: <Search />,
                    }}
                    {...formTextFieldProps}
                    value={symbol ?? ''}
                    name={fieldNames.symbol}
                    validate={validate}
                />
            )}
            <Box sx={{ width: '100%' }}>{children}</Box>
        </React.Fragment>
    );
};
